/** Statuses where the admin workflow is fully closed (no staging / approve / reject). */
export const SFTP_TERMINAL_STATUSES = [
    'PROCESSED',
    'DLQ',
    'FAILED',
    'ADMIN_REJECTED',
] as const;

/** No Detail link on the transfers list. */
export const SFTP_CLOSED_LIST_STATUSES = ['PROCESSED', 'ADMIN_REJECTED', 'DLQ'] as const;

/** Failed after approve — admin can retry processing from Detail. */
export const SFTP_POST_APPROVE_FAILURE_STATUSES = [
    'BATCH_SETUP_FAILED',
    'BATCH_SETUP',
    'FAILED',
    'DSCSA_FAILED',
    'PARSE_FAILED',
] as const;

/** Still open for admin review (including legacy ARCHIVED_REMOTE rows). */
export const SFTP_ADMIN_WORKFLOW_STATUSES = [
    'AWAITING_ADMIN_INPUT',
    'ARCHIVED_REMOTE',
    'VALIDATED',
    'PREVIEW_EXTRACTED',
] as const;

/** Admin can re-run EPCIS validation from the saved local file. */
export const SFTP_REPROCESS_VALIDATION_STATUSES = [
    'DOWNLOADED',
    'FAILED',
    'FORMAT_UNKNOWN',
    'VALIDATION_FAILED',
    'AWAITING_ADMIN_INPUT',
    'VALIDATED',
    'PREVIEW_EXTRACTED',
] as const;

export function canRetryPostApprovalProcessing(status?: string, approvalStatus?: string): boolean {
    if (approvalStatus !== 'APPROVED') return false;
    if (!status) return false;
    return (SFTP_POST_APPROVE_FAILURE_STATUSES as readonly string[]).includes(status);
}

export function isSftpTransferClosed(status?: string, approvalStatus?: string): boolean {
    if (canRetryPostApprovalProcessing(status, approvalStatus)) {
        return false;
    }
    if (approvalStatus === 'REJECTED') return true;
    if (!status) return false;
    return (SFTP_CLOSED_LIST_STATUSES as readonly string[]).includes(status);
}

/** @deprecated Use isSftpTransferClosed for list Detail link */
export function isSftpTransferTerminal(status?: string, approvalStatus?: string): boolean {
    return isSftpTransferClosed(status, approvalStatus);
}

export function isSftpTransferEditable(status?: string, approvalStatus?: string): boolean {
    if (approvalStatus === 'APPROVED' || approvalStatus === 'REJECTED') {
        return false;
    }
    if (!status) return false;
    if ((SFTP_TERMINAL_STATUSES as readonly string[]).includes(status)) {
        return false;
    }
    return (SFTP_ADMIN_WORKFLOW_STATUSES as readonly string[]).includes(status);
}

/** Pre-approval workflow or post-approval failure retry — admin can edit and save staging. */
export function isSftpStagingEditable(status?: string, approvalStatus?: string): boolean {
    return (
        isSftpTransferEditable(status, approvalStatus) ||
        canRetryPostApprovalProcessing(status, approvalStatus)
    );
}

export function canReprocessValidation(status?: string): boolean {
    if (!status) return false;
    return (SFTP_REPROCESS_VALIDATION_STATUSES as readonly string[]).includes(status);
}

export function needsValidationAttention(status?: string): boolean {
    return status === 'DOWNLOADED';
}

export function sftpStatusBadgeClass(status?: string): string {
    switch (status) {
        case 'AWAITING_ADMIN_INPUT':
            return 'bg-amber-100 text-amber-900';
        case 'DOWNLOADED':
        case 'DETECTING_FORMAT':
        case 'VALIDATING':
            return 'bg-sky-100 text-sky-800';
        case 'ARCHIVED_REMOTE':
            return 'bg-blue-100 text-blue-800';
        case 'PROCESSED':
        case 'ADMIN_APPROVED':
            return 'bg-green-100 text-green-800';
        case 'VALIDATION_FAILED':
        case 'FAILED':
        case 'DLQ':
        case 'ADMIN_REJECTED':
        case 'FORMAT_UNKNOWN':
        case 'BATCH_SETUP_FAILED':
        case 'DSCSA_FAILED':
        case 'PARSE_FAILED':
            return 'bg-red-100 text-red-800';
        default:
            return 'bg-slate-100 text-slate-800';
    }
}

export function formatStatusLabel(status?: string): string {
    if (!status) return '—';
    switch (status) {
        case 'DOWNLOADED':
            return 'Downloaded (validation pending)';
        case 'DETECTING_FORMAT':
            return 'Detecting format';
        case 'VALIDATING':
            return 'Validating schema';
        case 'AWAITING_ADMIN_INPUT':
            return 'Awaiting admin input';
        case 'ARCHIVED_REMOTE':
            return 'Awaiting admin (file on SFTP archive)';
        case 'VALIDATION_FAILED':
            return 'Validation failed';
        case 'FORMAT_UNKNOWN':
            return 'Unknown format';
        case 'ADMIN_REJECTED':
            return 'Rejected';
        case 'PROCESSED':
            return 'Processed';
        case 'FAILED':
            return 'Failed';
        case 'BATCH_SETUP_FAILED':
            return 'Batch setup failed';
        case 'DSCSA_FAILED':
            return 'DSCSA check failed';
        case 'PARSE_FAILED':
            return 'Parse failed';
        case 'QUEUED':
            return 'Queued for processing';
        case 'BATCH_SETUP':
            return 'Creating batch';
        default:
            return status.replace(/_/g, ' ');
    }
}

export function formatApprovalLabel(approvalStatus?: string): string {
    if (!approvalStatus) return '—';
    switch (approvalStatus) {
        case 'PENDING':
            return 'Pending review';
        case 'APPROVED':
            return 'Approved';
        case 'REJECTED':
            return 'Rejected';
        default:
            return approvalStatus;
    }
}
